/**
 * Answers-step batch (2026-07-22): field-key-driven reads + validation for
 * the Answers step's mandatory questions. Mirrors lineItemValidation.js's
 * pattern - pure, exported, unit-testable, and shared between StepAnswers'
 * own submit-time checks and WizardContext's resume-time/RFP-gate temporal
 * revalidation (item 7), so there is exactly one implementation of "is this
 * date combination still valid" instead of two that can drift.
 *
 * MANDATORY_FIELD_KEYS_ORDER (item 6, amended): the fixed render/validation
 * order for the mandatory group - ship-to location, quantity, currency,
 * budget, required-by/start date, end date (services only), submission
 * deadline last (so the runway warnings below compare against an
 * already-known required date).
 */
import { normalizeQuestion } from '@/lib/businessQuestions';
import { checkDateRules } from '@/lib/dateValidationRules';

export const MANDATORY_FIELD_KEYS_ORDER = [
  'ship_to_location',
  'quantity',
  'rfp_currency',
  'budget',
  // required-by/start date and end_date are inserted per-procurement-type
  // by getMandatoryFieldKeysForType below - not fixed field_key names here,
  // since goods/services/mixed each need a different subset.
  'submission_deadline',
];

// Always-required field_keys, regardless of procurement type (mirrors
// backend routes/procurement.py's _ALWAYS_MANDATORY_FIELD_KEYS exactly -
// keep both lists in sync). quantity is deliberately NOT here - see
// getMandatoryFieldKeysForType below.
const ALWAYS_MANDATORY_FIELD_KEYS = ['ship_to_location', 'rfp_currency', 'budget', 'submission_deadline'];

// The type-appropriate date field_key(s), inserted into the render/
// validation order right before submission_deadline.
export const getRequiredDateFieldKey = (procurementType) =>
  procurementType === 'services' ? 'start_date' : 'need_by_date';

// services-engagement-models ruling (item A): quantity is mandatory for
// goods/mixed but NOT services - a services engagement may have no natural
// headline count at all (mirrors backend routes/procurement.py's
// _missing_mandatory_field_keys_check exactly - keep both in sync).
//
// boqPresent (PROC-000026 follow-up, 16fd26c frontend mirror): quantity is
// ALSO not mandatory for goods/mixed when a BOQ exists - quantity is a
// per-line BOQ fact then, and question_generator_agent is instructed (plus
// a deterministic backend strip) to never generate a headline quantity
// question in that case. Same boq_present definition as the backend's
// _missing_mandatory_field_keys_check(procurement_type, boq_present) - a
// non-empty boq_items list, nothing else. Default false preserves every
// existing call site's behavior untouched.
export const getMandatoryFieldKeysForType = (procurementType, boqPresent = false) => {
  const dateKeys = procurementType === 'services'
    ? ['start_date', 'end_date']
    : ['need_by_date'];
  const quantityKeys = (procurementType === 'services' || boqPresent) ? [] : ['quantity'];
  return ['ship_to_location', ...quantityKeys, 'rfp_currency', 'budget', ...dateKeys, 'submission_deadline'];
};

/** {index, value} for the business question with this field_key, or
 * {index: -1, value: undefined} if no such question exists. Never reads
 * positionally - always resolves the question first, then its answer. */
export const findAnswerByFieldKey = (businessQuestions, answers, fieldKey) => {
  const index = (businessQuestions || []).findIndex((q) => normalizeQuestion(q).field_key === fieldKey);
  if (index === -1) return { index: -1, value: undefined };
  return { index, value: (answers || {})[`q${index}`] };
};

/** Which of the mandatory field_keys for this procurement type have no
 * matching business question at all (a generation defect, not a blank
 * answer - see agents.py's generation-time contract check, the primary
 * guard against this ever happening). boqPresent - see
 * getMandatoryFieldKeysForType - must be sourced from persisted state
 * (boqItems, populated by loadProcurement on resume as well as a
 * same-session upload), never session-only memory, so a resumed BOQ
 * procurement is never wrongly re-blocked. */
export const findMissingMandatoryFieldKeys = (businessQuestions, procurementType, boqPresent = false) => {
  const present = new Set(
    (businessQuestions || [])
      .map((q) => normalizeQuestion(q).field_key)
      .filter(Boolean)
  );
  return getMandatoryFieldKeysForType(procurementType, boqPresent).filter((key) => !present.has(key));
};

// Re-exported for existing call sites/tests importing toIsoDateOrNull from
// this module - the canonical implementation now lives in
// dateValidationRules.js (date-validation-fixes batch, 2026-07-23).
export { toIsoDateOrNull } from '@/lib/dateValidationRules';

/** Best-effort numeric extraction from a legacy TEXT budget answer (a
 * procurement in flight before the numeric-input change shipped may have
 * one) - strips everything but digits/decimal point so the new numeric
 * input has a sane starting value instead of showing NaN/blank on resume.
 * Returns '' if nothing numeric survives; never throws. */
export const parseLegacyBudget = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === '') return '';
  if (typeof rawValue === 'number') return Number.isFinite(rawValue) ? String(rawValue) : '';
  const cleaned = String(rawValue).replace(/[^\d.]/g, '');
  if (!cleaned || Number.isNaN(Number(cleaned))) return '';
  return cleaned;
};

/**
 * Items 2/3/7: the one date-logic check shared by StepAnswers' submit-time
 * validation and WizardContext's resume-time/RFP-gate temporal
 * revalidation. Assumes the relevant mandatory questions exist (presence
 * is findMissingMandatoryFieldKeys' job, checked separately) - a field
 * that exists but is unanswered yet is silently skipped here (StepAnswers'
 * own "please answer this" check catches that first).
 *
 * Date-validation-fixes batch (2026-07-23): delegates to the shared
 * dateValidationRules.checkDateRules (the ONE implementation, also used at
 * Lines and the RFP-generation gate) - this function's own job is just
 * translating the answered field_key values into that call and mapping its
 * {hardStops, softAlerts} back into the shape StepAnswers.jsx already
 * consumes, so nothing downstream needed to change.
 *
 * Soft-alert severity batch (2026-07-26): SANT/SAST (need-by/start vs
 * today) are now surfaced here too, mapped to requiredSoonGap - the
 * deferral from the prior batch is lifted; the shared engine already
 * computed these, only this function's own filter was holding them back.
 *
 * Returns:
 *   hardBlock: { field, message } | null - hard-blocks Continue/RFP
 *     generation. Priority order matches checkDateRules' own push order:
 *     submission_deadline in the past, required-date in the past, end-date
 *     before start-date, required-date on/before the deadline.
 *   softWarnings: { requiredSoonGap, runwayGap, todayGap } - any may be
 *     null. All three are advisory (proceed-anyway dialogs), never
 *     returned alongside a hardBlock (checkDateRules never returns soft
 *     alerts on a field that already hard-stopped). StepAnswers.jsx orders
 *     its chain by severity (requiredSoonGap/HIGH, runwayGap/MEDIUM,
 *     todayGap/LOW), not by this object's key order.
 */
export const checkAnswersTemporalValidity = ({ businessQuestions, answers, procurementType, now = new Date() }) => {
  const result = { hardBlock: null, softWarnings: { requiredSoonGap: null, runwayGap: null, todayGap: null } };

  const requiredDateFieldKey = getRequiredDateFieldKey(procurementType);
  const isServices = procurementType === 'services';

  const deadlineAnswer = findAnswerByFieldKey(businessQuestions, answers, 'submission_deadline');
  const requiredAnswer = findAnswerByFieldKey(businessQuestions, answers, requiredDateFieldKey);
  const endAnswer = isServices ? findAnswerByFieldKey(businessQuestions, answers, 'end_date') : null;

  const { hardStops, softAlerts } = checkDateRules({
    itemType: isServices ? 'services' : 'goods',
    needBy: isServices ? undefined : requiredAnswer.value,
    start: isServices ? requiredAnswer.value : undefined,
    end: isServices ? endAnswer?.value : undefined,
    submission: deadlineAnswer.value,
    now,
  });

  if (hardStops.length > 0) {
    const stop = hardStops[0];
    // `code` carried through (consolidated fix batch, 2026-07-24, items 2/3):
    // item 2 needs it to append the rule-code suffix at the display layer;
    // item 3 needs it so a temporalBlock built from this hard stop can pick
    // the right narrative frame (genuinely-past-date vs relationship rule).
    result.hardBlock = { field: stop.field, message: stop.message, code: stop.code };
    return result;
  }

  const todayGap = softAlerts.find((a) => a.code === 'SAPT');
  if (todayGap) {
    result.softWarnings.todayGap = {
      deadlineValue: deadlineAnswer.value,
      daysUntilDeadline: todayGap.gapDays,
      // Consolidated fix batch (2026-07-24), item 1: the shared engine now
      // builds the full rich message (dates/gap embedded) - StepAnswers.jsx
      // renders THIS directly instead of hand-rolling its own sentence, so
      // fixing the copy once here reaches every surface.
      message: todayGap.message,
      code: todayGap.code,
    };
  }

  const runwayGap = softAlerts.find((a) => a.code === 'SAPN' || a.code === 'SAPS');
  if (runwayGap) {
    result.softWarnings.runwayGap = {
      deadlineValue: deadlineAnswer.value,
      earliestRequired: requiredAnswer.value,
      gapDays: runwayGap.gapDays,
      message: runwayGap.message,
      code: runwayGap.code,
    };
  }

  const requiredSoonGap = softAlerts.find((a) => a.code === 'SANT' || a.code === 'SAST');
  if (requiredSoonGap) {
    result.softWarnings.requiredSoonGap = {
      requiredValue: requiredAnswer.value,
      gapDays: requiredSoonGap.gapDays,
      message: requiredSoonGap.message,
      code: requiredSoonGap.code,
    };
  }

  return result;
};
